import { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import { CategoryBannerImage, CategoryProductNode, ProductDetailType, SeoBlock, SeoMetaTags } from 'constants/dataTypes';
import { getCategoryInfoFromProductSlug } from 'functions/getCategoryInfoFromProductSlug';
import { wcApiDataService } from 'functions/wcApi';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import { ParsedUrlQuery } from 'querystring';
import { generateImage } from 'utils/image.utils';
import { getPageHeaderData, getSeoBlockContent, getSeoMetaTags, getUrlByLocale, getCategoryBannerImages } from 'utils/common.utils';
import { queryGenericDatabase } from 'functions/notion';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { filterAccessoryProduct } from 'functions/filterProduct';

const CategoryComponent = dynamic(() => import('modules/category'));

const ProductComponent = dynamic(() => import('modules/product'));

interface CategoryInfo {
  name: string;
  slug: string;
}

interface ProductInfo {
  name: string;
  slug: string;
}

export default function ShopCategory({
  categoryInfo,
  productInfo,
  categoryBannerProps,
  products,
  product,
  pageType,
  productDetailList,
  seoBlock,
  accessories,
  seoMetaTags,
  categoryBannerImages
}: {
  categoryInfo: CategoryInfo;
  productInfo: ProductInfo;
  categoryBannerProps: CategoryBannerProps;
  products: CategoryProductNode[];
  product: any;
  pageType: 'category' | 'product';
  productDetailList: ProductDetailType[];
  seoBlock: SeoBlock;
  accessories: any;
  seoMetaTags: SeoMetaTags;
  categoryBannerImages: CategoryBannerImage[];
}) {
  return (
    <>
      {pageType === 'category' ? (
        <CategoryComponent
          categoryInfo={categoryInfo}
          categoryBannerProps={categoryBannerProps}
          products={products}
          seoBlock={seoBlock}
          categoryBannerImages={categoryBannerImages}
        />
      ) : (
        <ProductComponent
          categoryInfo={categoryInfo}
          product={product}
          productDetailList={productDetailList}
          productInfo={productInfo}
          seoBlock={seoBlock}
          accessories={accessories}
        />
      )}
      <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return {
      paths: [],
      fallback: 'blocking'
    };
  }

  const paths: (
    | string
    | {
      params: ParsedUrlQuery;
      locale?: string | undefined;
    }
  )[] = [];

  var accessoriesPaths: any = await wcApiDataService(
    'paths/accessories',
    'hasProducts&hasSubCategory',
    ''
  );

  //code for map accessoriesPaths to paths, it has country slugs like au,ca,nz and international that has category and product properties OR subcategory property

  if (!accessoriesPaths) {
    return {
      paths,
      fallback: 'blocking'
    };
  }
  for (const [country, data] of Object.entries(accessoriesPaths) as any) {
    data.map((pageData: any) => {
      if (pageData?.category) {
        paths.push({
          params: {
            slug: pageData.product
          },
          locale: country
        });
      }
      if (pageData?.subcategory) {
        paths.push({
          params: {
            slug: pageData.subcategory
          },
          locale: country
        });
      }
    });
  }

  return {
    paths,
    fallback: 'blocking'
  };
};

//function that get section name as string and returns an array of objects with section name and section id
const getAccessoriesSectionList = async (
  sectionName: string,
  locale: string
) => {
  //Retrieve product descriptions from Notion DB
  const bodyContent = JSON.stringify({
    and: [
      {
        property: 'Section',
        select: {
          equals: sectionName
        }
      },
      {
        property: 'Category',
        select: {
          equals: 'accessories'
        }
      }
    ]
  });

  let productDetailList = [];

  if (process.env.NOTION_PRODUCT_DETAIL_DB) {
    productDetailList = await queryGenericDatabase(
      process.env.NOTION_PRODUCT_DETAIL_DB,
      bodyContent,
      locale
    );
  }

  return productDetailList;
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  var accessories: any = await wcApiDataService(
    'cat/accessories',
    'hasProducts&hasSubCategory',
    locale
  );
  accessories = filterAccessoryProduct(accessories, locale);

  if (!accessories) {
    return {
      notFound: true
    };
  }

  const productCategorySlugs: string[] = accessories.sub_category_data.map(
    (categoryItem: any) => categoryItem.slug
  );


  const seoBlock = await getSeoBlockContent('accessories/' + params?.slug, locale);

  const seoMetaTags = await getSeoMetaTags('/shop/accessories/' + params?.slug, locale);

  if (productCategorySlugs.includes(params?.slug as string)) {
    let accessoriesSubCategory: any = await wcApiDataService(
      'cat/' + params?.slug,
      'hasProducts',
      locale
    );
    accessoriesSubCategory = filterAccessoryProduct(accessoriesSubCategory, locale);

    if (!accessoriesSubCategory) {
      return {
        notFound: true
      };
    }

    const products = accessoriesSubCategory.products;

    const categoryInfo: CategoryInfo = {
      name: accessoriesSubCategory.name,
      slug: accessoriesSubCategory.slug
    };
    const categoryBannerProps: CategoryBannerProps = {
      title: accessoriesSubCategory.name,
      description: accessoriesSubCategory.description,
      imageSrc: generateImage(
        categoryInfo.slug,
        'categories',
        'slug',
        'banner',
        'jpg'
      )
    };


    const notionPageHeaders = await getPageHeaderData('accessories/' + params?.slug, locale, categoryBannerProps);

    let postit_notes = [];
    if (accessoriesSubCategory.meta && accessoriesSubCategory.meta.postit_notes) {
      postit_notes = accessoriesSubCategory.meta.postit_notes;
    }

    const categoryBannerImages = await getCategoryBannerImages('accessories/' + params?.slug, locale);

    return {
      props: {
        categoryInfo: categoryInfo,
        categoryBannerProps: notionPageHeaders,
        products: products,
        pageType: 'category',
        seoBlock,
        seoMetaTags,
        postit_notes: postit_notes,
        categoryBannerImages
      }
    };
  }

  const filteredProd = accessories.products.reduce(
    (accumulator: any, currentValue: any) =>
      currentValue.slug == params?.slug ? currentValue : accumulator, null
  );

  if (!filteredProd) {
    return {
      notFound: true
    };
  }

  const categoryInfo: CategoryInfo = getCategoryInfoFromProductSlug(
    filteredProd.category
  ) as CategoryInfo;
  const productInfo: ProductInfo = {
    name: filteredProd.name,
    slug: filteredProd.slug
  };

  //Retrieve accessories descriptions from Notion DB
  //first check category name match, if not check product name match
  let productDetailList = await getAccessoriesSectionList(
    categoryInfo.name,
    locale!
  );
  if (productDetailList.length === 0) {
    productDetailList = await getAccessoriesSectionList(
      productInfo.name,
      locale!
    );
  }

  return {
    props: {
      categoryInfo: categoryInfo,
      productInfo: productInfo,
      product: filteredProd,
      pageType: 'product',
      productDetailList,
      seoBlock,
      seoMetaTags,
      accessories
    }
  };
};
